import React, { useState, useEffect, useRef } from 'react';
import { createCustomCompanion, updateCompanion, previewAvatar } from '../lib/companions';
import './CompanionCreator.css';

// Curated trait suggestions — taps insert into the chips list.
const TRAIT_SUGGESTIONS = [
  'wise', 'witty', 'gentle', 'direct', 'curious', 'patient',
  'mischievous', 'devout', 'sceptical', 'optimistic', 'protective',
  'playful', 'rigorous', 'warm',
];

const APPEARANCE_PLACEHOLDER =
  'e.g. middle-aged Indian woman, kind eyes, simple sari, soft warm light, ' +
  'modern digital painting';

// Curated starter prompts — taps overwrite the prompt textarea so the
// user has a working example to either generate directly or remix.
const PROMPT_SUGGESTIONS = [
  {
    label: 'Wise sage',
    text: 'an elderly Indian sage with a long white beard, calm gaze, simple ' +
      'saffron robe, warm golden light, mountain background, painterly style',
  },
  {
    label: 'Young teacher',
    text: 'a young Indian woman in her late 20s, kind eyes, neat braid, simple ' +
      'cotton kurta, soft daylight, library backdrop, modern digital painting',
  },
  {
    label: 'Hooded mystic',
    text: 'a mysterious hooded figure, glowing amber eyes, deep blue robes ' +
      'with embroidered patterns, dim candlelight, fantasy concept art style',
  },
  {
    label: 'Cheerful chef',
    text: 'a cheerful middle-aged Indian man, round face, twinkling eyes, white ' +
      'chef’s coat, warm kitchen background, vivid colours, illustrated style',
  },
];

function CompanionCreator({ onClose, onCreated, mode = 'create', initial = null }) {
  const isEdit = mode === 'edit' && !!initial;
  // In edit mode skip step 1's onboarding feel and jump straight to the
  // form pre-filled. Step 2 (appearance/avatar) is still reachable but
  // the user usually just wants to tweak description/traits.
  const [step, setStep] = useState(1);
  const [name, setName] = useState(isEdit ? (initial.name || '') : '');
  const [description, setDescription] = useState(isEdit ? (initial.description || '') : '');
  const [traits, setTraits] = useState(isEdit ? (Array.isArray(initial.traits) ? initial.traits : []) : []);
  const [traitDraft, setTraitDraft] = useState('');
  const [appearance, setAppearance] = useState(isEdit ? (initial.appearance || '') : '');
  const [skipAvatar, setSkipAvatar] = useState(false);
  // Opt-in flag for re-running the LLM persona meta-prompt on save.
  // Off by default — typo fixes / avatar swaps shouldn't burn ~500
  // tokens. The user ticks this when they actually changed the
  // character's behavior.
  const [regeneratePrompt, setRegeneratePrompt] = useState(false);

  // Preview-first flow: the user types the appearance prompt, hits
  // "Generate preview", and only commits to creating the character
  // once they like the image they see. `previewUrl` is the URL of the
  // image already uploaded to S3; we pass it to the create call so
  // the server doesn't re-generate. In edit mode this seeds from the
  // existing avatar so the preview tile isn't blank.
  const [previewUrl, setPreviewUrl] = useState(isEdit ? (initial.avatarUrl || null) : null);
  // The exact appearance prompt that produced previewUrl — used to
  // decide whether the user has edited the prompt since previewing
  // (in which case the preview is stale). In edit mode we don't know
  // what prompt produced the saved avatar, so we seed it with the
  // current appearance value — only an explicit "Regenerate preview"
  // will mark it stale.
  const [previewPrompt, setPreviewPrompt] = useState(isEdit ? (initial.appearance || '') : '');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState(null);  // 'preview' | 'creating'

  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  // First-touch on step 2: if the user hasn't typed an appearance prompt,
  // seed one from what we already know about the character (description +
  // traits) plus a sensible default style. This is just a starter — the
  // user can edit it freely before the preview fires. Suppressed in edit
  // mode — the user already has an appearance saved, don't blow it away.
  useEffect(() => {
    if (step !== 2) return;
    if (isEdit) return;
    if (appearance.trim() || skipAvatar) return;
    const desc = description.trim();
    const traitPart = traits.length ? `, ${traits.join(', ')}` : '';
    const seed = desc
      ? `${desc}${traitPart}. Photorealistic portrait, warm cinematic lighting, painterly style.`
      : (PROMPT_SUGGESTIONS[0]?.text || '');
    if (seed) setAppearance(seed);
    // Run only when step transitions to 2; subsequent edits to the form
    // shouldn't overwrite the user's prompt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Auto-generate the avatar the first time the user lands on step 2
  // with a non-empty prompt (including the seeded one above). Saves a
  // click on the happy path — they can still hit "Regenerate preview"
  // if they tweak the prompt later. Guards: never auto-fire if they've
  // checked "Skip avatar", if a preview already exists, or if a request
  // is already in flight. Also skipped in edit mode — they already have
  // an avatar; only an explicit Regenerate should cost an image gen.
  useEffect(() => {
    if (step !== 2) return;
    if (isEdit) return;
    if (previewUrl || skipAvatar || busy) return;
    if (!appearance.trim()) return;
    doPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, appearance]);

  const addTrait = (t) => {
    const v = (t || '').trim().slice(0, 40);
    if (!v) return;
    setTraits((ts) => ts.includes(v) ? ts : [...ts, v].slice(0, 8));
    setTraitDraft('');
  };
  const removeTrait = (t) => setTraits((ts) => ts.filter((x) => x !== t));

  const canCreate = name.trim().length > 0 && !busy;
  const previewStale = !!previewUrl && previewPrompt !== appearance.trim();

  // Generate / regenerate the avatar preview. Counts against the image
  // quota each time it runs. On success, the URL is stored locally —
  // we don't create the companion row yet.
  const doPreview = async () => {
    const prompt = appearance.trim();
    if (!prompt || busy) return;
    setBusy(true);
    setError(null);
    setPhase('preview');
    try {
      const out = await previewAvatar({ appearance: prompt });
      if (out?.avatarUrl) {
        setPreviewUrl(out.avatarUrl);
        setPreviewPrompt(prompt);
      } else {
        setError('Preview failed. Try again or skip the avatar.');
      }
    } catch (err) {
      console.warn('[avatar-preview]', err.message);
      setError(err.message || 'Could not generate a preview.');
    } finally {
      setBusy(false);
      setPhase(null);
    }
  };

  // Create or update the companion row. Edit branch sends a PATCH with
  // only the editable fields (the server ignores anything else). Create
  // branch is unchanged.
  const submit = async () => {
    if (!canCreate) return;
    setBusy(true);
    setError(null);
    setPhase(isEdit ? 'saving' : 'creating');
    try {
      // Only attach the preview URL if it isn't stale (prompt edited
      // since the preview was generated).
      const avatarToUse = skipAvatar || previewStale ? undefined : (previewUrl || undefined);
      let c;
      if (isEdit) {
        c = await updateCompanion(initial.companionId, {
          name: name.trim(),
          description: description.trim(),
          traits,
          appearance: appearance.trim(),
          // Only send avatarUrl if the user actually regenerated it
          // (previewUrl differs from the original). Otherwise leave the
          // saved one alone.
          avatarUrl: avatarToUse && avatarToUse !== initial.avatarUrl
            ? avatarToUse
            : undefined,
          regeneratePrompt,
        });
      } else {
        c = await createCustomCompanion({
          name: name.trim(),
          description: description.trim(),
          traits,
          appearance: appearance.trim(),
          avatarUrl: avatarToUse,
        });
      }
      onCreated?.(c);
    } catch (err) {
      setError(err.message || (isEdit ? 'Could not save changes.' : 'Could not create this character.'));
      setBusy(false);
      setPhase(null);
    }
  };

  return (
    <div
      ref={overlayRef}
      className="mitra-creator-overlay"
      onClick={(e) => { if (e.target === overlayRef.current && !busy) onClose?.(); }}
    >
      <div className="mitra-creator">
        <button
          type="button"
          className="mitra-creator-close"
          onClick={() => !busy && onClose?.()}
          disabled={busy}
          aria-label="Close"
        >
          <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path d="M18 6 6 18M6 6l12 12"/>
          </svg>
        </button>

        <div className="mitra-creator-head">
          <h2>{isEdit ? 'Edit character' : 'Create a character'}</h2>
          <p>
            {isEdit
              ? 'Update what changes about them. Tick "Regenerate persona" if you want their behaviour to reflect the new description.'
              : "Give your character a name, a few traits, and an appearance — they'll be there waiting."}
          </p>
        </div>

        {step === 1 && (
          <div className="mitra-creator-body">
            {isEdit && (
              <div className="mc-edit-avatar">
                <div className="mc-edit-avatar-tile">
                  {previewUrl ? (
                    <img src={previewUrl} alt="Current avatar" />
                  ) : (
                    <div className="mc-edit-avatar-fallback">
                      {(name || '?').trim().charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="mc-edit-avatar-meta">
                  <div className="mc-edit-avatar-title">Character image</div>
                  <div className="mc-edit-avatar-hint">
                    Tap below to tweak the prompt and generate a new picture.
                  </div>
                  <button
                    type="button"
                    className="mc-btn mc-btn-primary mc-edit-avatar-btn"
                    onClick={() => setStep(2)}
                    disabled={busy}
                  >
                    Improve image →
                  </button>
                </div>
              </div>
            )}

            <label className="mc-field">
              <span className="mc-label">Name</span>
              <input
                autoFocus
                className="mc-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Meera"
                maxLength={40}
                disabled={busy}
              />
            </label>

            <label className="mc-field">
              <span className="mc-label">
                Who are they?
                <span className="mc-hint">A line or two — backstory, voice, what they care about.</span>
              </span>
              <textarea
                className="mc-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A patient music teacher from Pune who listens before advising. Speaks softly, asks good questions."
                rows={3}
                disabled={busy}
              />
            </label>

            <div className="mc-field">
              <span className="mc-label">
                Traits
                <span className="mc-hint">Pick a few, or type your own.</span>
              </span>
              <div className="mc-traits">
                {traits.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="mc-trait-chip on"
                    onClick={() => removeTrait(t)}
                    disabled={busy}
                  >
                    {t} ×
                  </button>
                ))}
                <input
                  className="mc-trait-input"
                  value={traitDraft}
                  onChange={(e) => setTraitDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      addTrait(traitDraft);
                    }
                  }}
                  placeholder={traits.length ? '' : 'Add a trait…'}
                  maxLength={40}
                  disabled={busy || traits.length >= 8}
                />
              </div>
              <div className="mc-suggest">
                {TRAIT_SUGGESTIONS
                  .filter((t) => !traits.includes(t))
                  .slice(0, 10)
                  .map((t) => (
                    <button
                      key={t}
                      type="button"
                      className="mc-trait-chip"
                      onClick={() => addTrait(t)}
                      disabled={busy || traits.length >= 8}
                    >
                      + {t}
                    </button>
                  ))}
              </div>
            </div>

            {isEdit && (
              <label className="mc-skip" style={{ marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={regeneratePrompt}
                  onChange={(e) => setRegeneratePrompt(e.target.checked)}
                  disabled={busy}
                />
                <span>
                  Regenerate persona prompt so their voice picks up the new description / traits
                  <span className="mc-hint" style={{ display: 'block', marginLeft: 0 }}>
                    Costs a small amount of tokens. Skip if you only fixed a typo or changed the avatar.
                  </span>
                </span>
              </label>
            )}

            <div className="mitra-creator-actions">
              <button
                type="button"
                className="mc-btn mc-btn-secondary"
                onClick={() => !busy && onClose?.()}
                disabled={busy}
              >
                Cancel
              </button>
              {!isEdit && (
                <button
                  type="button"
                  className="mc-btn mc-btn-primary"
                  onClick={() => name.trim() && setStep(2)}
                  disabled={!name.trim() || busy}
                >
                  Next: appearance
                </button>
              )}
              {isEdit && (
                <button
                  type="button"
                  className="mc-btn mc-btn-primary"
                  onClick={submit}
                  disabled={!canCreate || (busy && phase === 'saving')}
                >
                  {busy && phase === 'saving' ? 'Saving…' : 'Save changes'}
                </button>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="mitra-creator-body mc-step2">
            {/* ── Two-column layout: prompt on the left, preview on
                the right. Stacks on narrow screens via CSS. ── */}
            <div className="mc-step2-grid">
              <div className="mc-step2-prompt">
                <label className="mc-field">
                  <span className="mc-label">
                    Prompt
                    <span className="mc-hint">Describe how they should look — this drives the avatar image.</span>
                  </span>
                  <textarea
                    autoFocus
                    className="mc-textarea"
                    value={appearance}
                    onChange={(e) => setAppearance(e.target.value)}
                    placeholder={APPEARANCE_PLACEHOLDER}
                    rows={5}
                    maxLength={600}
                    disabled={busy}
                  />
                </label>

                <div className="mc-prompt-suggest">
                  <span className="mc-prompt-suggest-label">Try one:</span>
                  {PROMPT_SUGGESTIONS.map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      className="mc-prompt-chip"
                      onClick={() => setAppearance(s.text)}
                      disabled={busy}
                      title={s.text}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="mc-btn mc-btn-primary mc-preview-btn"
                  onClick={doPreview}
                  disabled={busy || !appearance.trim() || skipAvatar}
                >
                  {busy && phase === 'preview'
                    ? 'Generating preview…'
                    : (previewUrl ? 'Regenerate preview' : 'Generate preview')}
                </button>

                <label className="mc-skip">
                  <input
                    type="checkbox"
                    checked={skipAvatar}
                    onChange={(e) => {
                      setSkipAvatar(e.target.checked);
                      if (e.target.checked) { setPreviewUrl(null); setPreviewPrompt(''); }
                    }}
                    disabled={busy}
                  />
                  <span>Skip avatar — use the default initial instead</span>
                </label>
              </div>

              <div className="mc-step2-preview">
                <div className="mc-label">Preview</div>
                <div className={`mc-preview-tile ${busy && phase === 'preview' ? 'is-busy' : ''}`}>
                  {previewUrl ? (
                    <img src={previewUrl} alt="Avatar preview" />
                  ) : (
                    <div className="mc-preview-empty">
                      {skipAvatar
                        ? <>No avatar — they'll show as <strong>{(name || '?').trim().charAt(0).toUpperCase()}</strong>.</>
                        : <>Write an appearance prompt and tap <strong>Generate preview</strong> to see your character.</>}
                    </div>
                  )}
                  {busy && phase === 'preview' && <div className="mc-preview-spinner">Painting…</div>}
                </div>
                {previewStale && (
                  <div className="mc-preview-stale">
                    Prompt changed — regenerate to update the preview.
                  </div>
                )}
              </div>
            </div>

            {error && <div className="mc-error">{error}</div>}

            <div className="mitra-creator-actions">
              <button
                type="button"
                className="mc-btn mc-btn-secondary"
                onClick={() => !busy && setStep(1)}
                disabled={busy}
              >
                Back
              </button>
              <button
                type="button"
                className="mc-btn mc-btn-primary"
                onClick={submit}
                disabled={!canCreate || (busy && (phase === 'creating' || phase === 'saving'))}
                title={!previewUrl && !skipAvatar
                  ? 'Generate a preview first, or check "Skip avatar"'
                  : ''}
              >
                {busy && phase === 'creating' ? 'Creating…'
                  : busy && phase === 'saving' ? 'Saving…'
                  : isEdit ? 'Save changes' : 'Create character'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CompanionCreator;
